import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import useStudySession from 'hooks/useStudySession';
import { DISABLE_TEST_REPORT } from 'global/constants';
import { setError } from 'store/error';
import { createTimerWorker } from 'workers/createTimerWorker';
import DigitalSATInstruction from './Instruction';
import DigitalSATModule from './Module';
import DigitalSATBreak from './Break';
import { TTSProvider } from 'contexts/TTSContext';
import ReadAloudPanel from 'components/ReadAloudPanel';
import './index.css';

const SUBJECT_ABBR = {
    'Reading and Writing': 'RW',
    'Math': 'Math',
    'ACT Reading': 'Reading',
    'ACT Mathematics': 'Math',
    'ACT English': 'English',
    'ACT Science': 'Science',
    'Mathematics': 'Math',
    'English Language Arts': 'English'
};

function DigitalSAT() {
    const [isLoading, setIsLoading] = useState(true);
    const [instruction, setInstruction] = useState(true);
    const [testName, setTestName] = useState('');
    const [numTestSections, setNumTestSections] = useState(0);
    const [testSections, setTestSections] = useState([]);
    const [testReview, setTestReview] = useState(true);
    const [ttsEnabled, setTtsEnabled] = useState(false);
    const [moduleId, setModuleId] = useState('');
    const [moduleName, setModuleName] = useState('');
    const [moduleNumber, setModuleNumber] = useState(0);
    const [moduleSubject, setModuleSubject] = useState(''); 
    const [modulePrompt, setModulePrompt] = useState('');
    const [breakAfter, setBreakAfter] = useState(false);
    const [moduleBreak, setModuleBreak] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState([]);
    // const [moduleDuration, setModuleDuration] = useState(null);
    const [moduleTime, setModuleTime] = useState(null);
    const [questionTime, setQuestionTime] = useState(null);

    const { user } = useAppSelector(state => state.user);
    const { organization } = useAppSelector(state => state.organization);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { testId } = useParams();

    const timerWorkerRef = useRef(null);

    useStudySession({
        activityType: 'test',
        activityIds: { studentTestId: testId },
    });

    const getNextModule = () => {
        setIsLoading(true);
        // setModuleDuration(null);
        setModuleTime(null);
        axios.get(`tests/digital/sat/${testId}/`)
            .then(res => {
                if (res.data.id) {
                    setTestName(res.data.testName);
                    setNumTestSections(res.data.numTestSections);
                    setTestSections(res.data.testSections);
                    setTestReview(res.data.testReview);
                    setTtsEnabled(res.data.ttsEnabled ?? false);
                    setModuleId(res.data.id);
                    setModuleName(res.data.name);
                    setModuleNumber(res.data.number);
                    setModuleSubject(res.data.subject);
                    // setModuleDuration(res.data.duration);
                    setModuleTime(res.data.duration);
                    setModulePrompt(res.data.prompt);
                    setModuleBreak(false);
                    setBreakAfter(res.data.breakAfter);
                    setQuestions(res.data.questions);
                    setAnswers(res.data.answers);
                    setQuestionIndex(0);
                    setQuestionTime(0);

                    if (localStorage.getItem('autosave')) {
                        const autosave = JSON.parse(localStorage.getItem('autosave'));
                        if (autosave.moduleId === res.data.id) {
                            setAnswers(autosave.answers);

                            // setModuleDuration(autosave.moduleTime);
                            setModuleTime(autosave.moduleTime);
                        }
                    }

                    setIsLoading(false);
                }
                else {
                    if (!testReview || DISABLE_TEST_REPORT.includes(organization.alias)) {
                        // navigate('/tests/digital-sat');
                        navigate('/tests');
                    }
                    else {
                        navigate(`/report/digital-sat/${testId}`);
                    }
                    
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }

    const sendReport = () => {
        axios.post('debug/', {
            email: user.email,
            message: JSON.stringify({
                module: `${testName} ${moduleName}`,
                answers: answers,
                // moduleDuration: moduleDuration,
                moduleTime: moduleTime
            })
        });
    }

    const beginTest = () => {
        setInstruction(false);
        setQuestionTime(0);
        window.scrollTo(0, 0);
    }

    const autoSave = () => {
        localStorage.setItem('autosave', JSON.stringify({
            moduleId: moduleId,
            answers: answers,
            moduleTime: moduleTime
        }));
    }

    const navigateQuestion = (index: number) => {
        if (questionIndex >= 0 && questionIndex < questions.length) {
            let newAnswers = [...answers];
            newAnswers[questionIndex].timeSpent += questionTime;
            setAnswers(newAnswers);
        }
        setQuestionTime(0);
        setQuestionIndex(index);

        autoSave();
    }

    const setAnswer = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].answer = answer;
        setAnswers(newAnswers);

        autoSave();
    }

    const setChoice = (answer: string) => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].answer = answers[questionIndex].answer == answer
            ? ''
            : answer;

        newAnswers[questionIndex].crossedOut = newAnswers[questionIndex].crossedOut.filter(value => value !== answer);
        setAnswers(newAnswers);

        autoSave();
    }

    const markQuestion = () => {
        let newAnswers = [...answers];
        newAnswers[questionIndex].marked = !answers[questionIndex].marked;
        setAnswers(newAnswers);

        autoSave();
    }

    const crossOutChoice = (choiceId: string) => {
        let newAnswers = [...answers];
        if (newAnswers[questionIndex].crossedOut.includes(choiceId)) {
            newAnswers[questionIndex].crossedOut = newAnswers[questionIndex].crossedOut.filter(value => value !== choiceId);
        }
        else {
            newAnswers[questionIndex].crossedOut.push(choiceId);
        }
        if (choiceId === answers[questionIndex].answer) {
            newAnswers[questionIndex].answer = '';
        }
        setAnswers(newAnswers);

        autoSave();
    }

    const saveAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
        isStem?: boolean;
    }) => {
        let newAnswers = [...answers];
        const index = answers[questionIndex].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionIndex].annotations[index] = annotation;
        }
        else {
            const coveredAnnotations = answers[questionIndex].annotations.filter(existing => (
                existing.passageId === annotation.passageId &&
                existing.startIndex >= annotation.startIndex &&
                existing.endIndex <= annotation.endIndex
            ));

            newAnswers[questionIndex].annotations.push(annotation);

            for (let i = 0; i < coveredAnnotations.length; i++) {
                const coveredIndex = newAnswers[questionIndex].annotations.findIndex(existing => (
                    existing.passageId === coveredAnnotations[i].passageId &&
                    existing.startIndex === coveredAnnotations[i].startIndex &&
                    existing.endIndex === coveredAnnotations[i].endIndex
                ));

                const coveredAnnotation = newAnswers[questionIndex].annotations.splice(coveredIndex, 1)[0];
                newAnswers[questionIndex].annotations.push(coveredAnnotation);
            }
        }
        setAnswers(newAnswers);
        autoSave();
    }

    const deleteAnnotation = (annotation: {
        passageId: string;
        startIndex: number;
        endIndex: number;
        annotation: string;
        html?: string;
        preview?: string;
        color?: string;
        underline?: string;
        isStem?: boolean;
    }) => {
        let newAnswers = [...answers];
        const index = answers[questionIndex].annotations.findIndex(existing => (
            existing.passageId === annotation.passageId &&
            existing.startIndex === annotation.startIndex &&
            existing.endIndex === annotation.endIndex
        ));
        if (index >= 0) {
            newAnswers[questionIndex].annotations.splice(index, 1);
        }
        setAnswers(newAnswers);

        autoSave();
    }

    const handleSubmit = () => {
        let newAnswers = [...answers];
        if (questionIndex >= 0 && questionIndex < questions.length) {
            newAnswers[questionIndex].timeSpent += questionTime;
        }
        setIsLoading(true);
        sendReport();
        axios.post(`tests/digital/submit/sat/${testId}/${moduleId}/`, {
                answers: newAnswers,
                timeLeft: moduleTime
            })
            .then(() => {
                setModuleTime(null);
                setQuestionTime(null);
                setIsLoading(false);
                if (timerWorkerRef.current) {
                    timerWorkerRef.current.postMessage({ type: 'stop' });
                    timerWorkerRef.current.terminate();
                    timerWorkerRef.current = null;
                }
                if (breakAfter) {
                    setBreakAfter(false);
                    setModuleBreak(true);
                }
                else {
                    getNextModule();
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }

    // Keep a ref to the latest handleSubmit so the timer worker (whose onmessage
    // closure is wired only once) always submits LIVE answers, not a stale snapshot.
    const handleSubmitRef = useRef(handleSubmit);
    useEffect(() => { handleSubmitRef.current = handleSubmit; });

    const handleSaveAndExit = () => {
        setIsLoading(true);
        axios.post(`tests/digital/save/sat/${testId}/${moduleId}/`, {
                answers: answers,
                timeLeft: moduleTime
            })
            .then(() => {
                // exit
                if (timerWorkerRef.current) {
                    timerWorkerRef.current.postMessage({ type: 'stop' });
                    timerWorkerRef.current.terminate();
                    timerWorkerRef.current = null;
                }
                navigate(-1);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }
    
    const handleDiscardAndExit = () => {
        localStorage.removeItem('autosave');
        navigate(-1);
    }

    useEffect(() => {
        getNextModule();
    }, []);

    // useEffect(() => {
    //     if (!instruction) {
    //         if (moduleDuration !== null) {
    //             setModuleTime(moduleDuration);
    //         } else {
    //             setModuleTime(null);
    //         }
    //     }
    // }, [instruction, moduleDuration]);

    useEffect(() => {
        if (isLoading || instruction) return;
        // if (moduleTime === null || questionTime === null) return;

        if (timerWorkerRef.current) {
            timerWorkerRef.current.postMessage({ type: 'stop' });
            timerWorkerRef.current.terminate();
            timerWorkerRef.current = null;
        }

        timerWorkerRef.current = createTimerWorker();;

        // console.log(timerWorkerRef.current);

        timerWorkerRef.current.onmessage = (event) => {
            if (event.data === 'tick') {
                // console.log('tick');
                setModuleTime(prev => {
                    if (prev === null) return prev;
                    else if (prev <= 1) {
                        handleSubmitRef.current();
                        return 0;
                    }
                    return prev - 1;
                });
                setQuestionTime(prev => {
                    if (prev === null) return prev;
                    else return prev + 1
                });
            }
        }

        timerWorkerRef.current.postMessage({ type: 'start', delay: 1000 });

        return () => {
            if (timerWorkerRef.current) {
                timerWorkerRef.current.postMessage({ type: 'stop' });
                timerWorkerRef.current.terminate();
                timerWorkerRef.current = null;
            }
        }
    }, [isLoading, instruction]);

    return (
        <div className='digital' onCopy={(event) => { event.preventDefault() }}>
            <Helmet>
                <title>
                    {testName ? testName : 'Digital SAT Practice Test'} - {organization.name}
                </title>
            </Helmet>
            {isLoading
            ?   <Loader />
            :   <div className={moduleNumber && moduleBreak
                    ? 'digital__box--break'
                    : 'digital__box'
                }>
                    {instruction
                    ?   <DigitalSATInstruction
                            testName={testSections.length === numTestSections
                                ? testName
                                : testSections.length === 1
                                ? `${testName} - ${SUBJECT_ABBR[testSections[0].subject]} Only`
                                : `${testName} - ${testSections.map(section => SUBJECT_ABBR[section.subject]).join(', ')}`
                            }
                            beginTest={beginTest}
                        />
                    :   (moduleBreak
                        ?   <TTSProvider enabled={ttsEnabled} resetKey={`break:${moduleId ?? ''}`}>
                                <DigitalSATBreak
                                    endBreak={getNextModule}
                                />
                                {ttsEnabled && <ReadAloudPanel />}
                            </TTSProvider>
                        :   <TTSProvider enabled={ttsEnabled} resetKey={`${moduleId}:${questionIndex}`}>
                                <DigitalSATModule
                                    moduleName={moduleName}
                                    subject={moduleSubject}
                                    timeLeft={moduleTime}
                                    isUntimed={moduleTime===null? true: false}
                                    prompt={modulePrompt}
                                    questions={questions}
                                    questionIndex={questionIndex}
                                    answers={answers}
                                    ttsEnabled={ttsEnabled}
                                    markQuestion={markQuestion}
                                    crossOutChoice={crossOutChoice}
                                    saveAnnotation={saveAnnotation}
                                    deleteAnnotation={deleteAnnotation}
                                    handleChange={value => setAnswer(value)}
                                    handleSelect={value => setChoice(value)}
                                    navigateQuestion={navigateQuestion}
                                    handleSaveAndExit={handleSaveAndExit}
                                    handleDiscardAndExit={handleDiscardAndExit}
                                    handleSubmit={handleSubmit}
                                />
                                {ttsEnabled && <ReadAloudPanel />}
                            </TTSProvider>
                        )
                    }
                </div>
            }
        </div>
    )
}

export default DigitalSAT;